import React, { useEffect, useState } from "react";
import SlideoverModal from "src/components/SlideoverModal";
import Button from "src/base-components/Button";
import {
  clearUpdateCall,
  customerInfo,
  postCallCustomerInfo,
  queueCalls,
  queueTransferCalls,
  selectCustomerInfo,
  selectUpdateCall,
  updateCallLog,
  updateTransferCallLog,
} from "src/stores/calllogs";
import { RootState, useAppDispatch, useAppSelector } from "src/stores";
import Lucide from "src/base-components/Lucide";
import { clearCallLogsToasts } from "src/stores/callLogsToasts/slice";
import LoadingIcon from "src/base-components/LoadingIcon";
import CreateQuery from "src/components/CallsLogsModal/callAnswered/CreateQuery";
import ViewQueryModal from "src/components/CallsLogsModal/callAnswered/ViewQueryModal";
import { authUserSlice } from "src/stores/auth";
import { useSelector } from "react-redux";

type RemarkType = "transfer_call" | "disconnect_call";

interface Props {
  visible: boolean;
  onClose: () => void;
  direction: string | null;
  getCallerNum?: string | null;
  getSessionID: string | null;
  remarkType?: RemarkType;
  queryId?: string;
}

/* ─── Or divider ─── */
const OrDivider = () => (
  <div className="flex items-center gap-3 my-1">
    <div className="flex-1 h-px bg-slate-200" />
    <span className="text-xs text-slate-400">Or</span>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
);

const PostCallRemarks: React.FC<Props> = ({
  visible,
  onClose,
  getCallerNum,
  getSessionID,
  remarkType = "disconnect_call",
  queryId = ""
}) => {
  const [selectedQueryID, setSelectedQueryID] = useState<string>("");
  const [showCreateQuery, setShowCreateQuery] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewQueryID, setViewQueryID] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [notesErr, setNotesErr] = useState("");
  const dispatch = useAppDispatch();
  const { loading, data } = useAppSelector(selectUpdateCall);
  const getCustomerInfo = useAppSelector(selectCustomerInfo);
  const InfoCustomer = getCustomerInfo?.data?.result?.customer;
  const queriesInfo = getCustomerInfo?.data?.result?.queries;

  /* ── clear stale state on open ── */
  useEffect(() => {
    if (visible) dispatch(clearUpdateCall());
  }, [visible, dispatch]);

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  /* ── fetch customer ── */
  useEffect(() => {
    if (getCallerNum && remarkType === "disconnect_call") dispatch(customerInfo(getCallerNum));
    if(remarkType === "transfer_call" && getSessionID){
      dispatch(postCallCustomerInfo(getSessionID));
    }
  }, [getCallerNum, remarkType]);

  /* ── prefill notes from cache ── */
  useEffect(() => {
    if (!getSessionID) return;
    try {
      const cached = localStorage.getItem(`callRemarks_${getSessionID}`);
      if (cached?.trim() && !notes) setNotes(cached);
    } catch (_) { }
  }, [getSessionID]);

  useEffect(() => {
    if (!getSessionID || notes.trim()) return;
    try {
      const raw = localStorage.getItem(`callDetail_${getSessionID}`);
      if (raw) {
        const remarks = JSON.parse(raw)?.call?.remarks;
        if (typeof remarks === "string" && remarks.trim()) setNotes(remarks);
      }
    } catch (_) { }
  }, [getSessionID]);

  /* ── success handler ── */
  useEffect(() => {
    if (data?.success === true) {
      onClose();
      dispatch(clearUpdateCall());
      dispatch(clearCallLogsToasts());
      // dispatch(queueCalls());
      if(remarkType === "transfer_call"){
        dispatch(queueTransferCalls());
      }
      else{
        dispatch(queueCalls());
      }
      resetForm();
      if (getSessionID)
        localStorage.removeItem(`callRemarks_${getSessionID}`);
    }
  }, [data?.success]);

  const resetForm = () => {
    setSelectedQueryID("");
    setNotes("");
    setNotesErr("");
  };

  /* ── toggle select / unselect ── */
  const handleToggleQuery = (id: string) =>
    setSelectedQueryID((prev) => (prev === id ? "" : id));

  /* ── submit ── */
  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    setNotesErr("");
    // if (!getSessionID) return;
    if (!notes.trim()) { setNotesErr("Please enter remarks"); return; }
    if (notes.trim().length < 10) {
      setNotesErr("Remarks must be at least 10 characters");
      return;
    }

    if (remarkType === "transfer_call") {
      dispatch(updateTransferCallLog({ id: queryId, transfer_remark: notes }));
    }
    else if (getSessionID)  {
      dispatch(
        updateCallLog(getSessionID, {
          customer_id: InfoCustomer?.customer_id ?? "",
          query_id: selectedQueryID ?? "",
          remarks: notes,
          call_type: "Answered",
          call_outcome: "",
          queue: 0,
          is_new_query: null,
          session_id: getSessionID,
        })
      );
    }
  };

  const handleSkip = () => { onClose(); resetForm(); };

  return (
    <SlideoverModal
      visible={visible}
      onClose={onClose}
      size="lg"
      backdrop={false}
      headerTitle={
        <div className="flex items-center w-full">
          <div className="text-[15px] font-semibold text-slate-900 flex-grow ">
            Post-Call Remarks
          </div>
          {/* Skip Button aligned to the right */}
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-600 px-3 py-1.5 rounded-md transition-colors ml-[360px] "
          >
            Skip
          </button>
        </div>
      }
    >    {getCustomerInfo.loading ? (
      <div className="flex items-center justify-center h-40">
        <LoadingIcon />
      </div>
    ) : (
      <div className="flex flex-col gap-5">

        {/* ── Caller Information ── */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-900 mb-2.5">
            Caller Information
          </p>
          <div className="flex items-center">
            <div className="flex items-center gap-1.5 text-[13px] text-slate-700">
              <Lucide icon="Phone" className="w-[15px] h-[15px] text-slate-500" />
              {remarkType === "transfer_call" ? `${InfoCustomer?.country_code}${InfoCustomer?.phone}` : getCallerNum ?? "--"}
            </div>
            <div className="mx-3.5 w-px h-4 bg-slate-200" />
            <div className="flex items-center gap-1.5 text-[13px] text-slate-700">
              <Lucide icon="User" className="w-[15px] h-[15px] text-slate-500" />
              {InfoCustomer?.fullname ?? "New Customer"}
            </div>
          </div>
        </div>

        {/* ── Choose Existing Query ── */}
        {queriesInfo && queriesInfo.length > 0 && (
          <div>
            <p className="text-[14px] font-semibold text-slate-900 mb-3">
              {remarkType === "disconnect_call" ? "Choose" : "View"} Existing Query
            </p>

            <div className="grid grid-cols-2 gap-3">
              {queriesInfo.map((query) => {
                const isSelected = selectedQueryID === query.id;
                const total =
                  (Number(query?.no_of_adults) || 0) +
                  (Number(query?.no_of_infants) || 0) +
                  (Number(query?.no_of_children) || 0);

                return (
                  <div
                    key={query.id}
                    onClick={() => handleToggleQuery(query.id)}
                    className={`rounded-[10px] p-3.5 cursor-pointer transition-all duration-150 ${isSelected
                      ? "border border-blue-500 bg-blue-50"
                      : "border border-slate-500 bg-white hover:border-slate-300"
                      }`}
                  >
                    {/* Query no + badge */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] font-semibold text-blue-600">
                        {query?.query_no}
                      </span>
                      <span
                        className="text-[11px] font-medium px-2.5 py-0.5 rounded-sm bg-blue-300/50 text-blue-800"

                      >
                        {query?.status_text}
                      </span>
                    </div>

                    {/* Name */}
                    <p className="text-[13px] font-semibold text-slate-900 mb-1.5">
                      {InfoCustomer?.fullname}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-3">
                      <span>Customer ID: {InfoCustomer?.customer_id}</span>
                      <span>No. of PAX: {total || "-"}</span>
                    </div>

                    {/* ── Select / Unselect   View ── */}
                    <div className="flex items-center justify-between  gap-2">
                      {/* Select / Unselect button */}
                      {remarkType === "disconnect_call" && <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleQuery(query.id);
                        }}
                        className={`text-[12px] font-semibold py-[7px] px-[18px] rounded-md text-white bg-[#233580] transition-colors border-none ${isSelected
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "text-white hover:bg-[#233580]"
                          }`}
                        style={
                          !isSelected
                            ? { backgroundColor: "" }
                            : undefined
                        }
                      >
                        {isSelected ? "Unselect" : "Select"}
                      </button>
                      }

                      {/* View button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewQueryID(query.id);
                          setOpenViewModal(true);
                        }}
                        className="text-[12px] font-semibold py-[7px] px-[18px] rounded-md text-white bg-[#233580] transition-colors border-none"

                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <OrDivider />

        {/* ── Create New Query ── */}
        {remarkType === "disconnect_call" && <button
          type="button"
          onClick={() => setShowCreateQuery(true)}
          className="w-full flex items-center justify-center gap-1.5 py-3 border border-slate-200 rounded-lg bg-slate-50 hover:bg-slate-100 text-[13px] font-medium text-slate-700 transition-colors"
        >
          <Lucide icon="Plus" className="w-3.5 h-3.5 text-slate-500" />
          Create New Query
        </button>
        }

        {remarkType === "disconnect_call" && <OrDivider />}

        {/* ── Remarks ── */}
        <div>
          <label className="block text-[13px] font-semibold text-slate-900 mb-2">
            Remarks
          </label>
          <textarea
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setNotesErr(""); }}
            placeholder="Type your query here..."
            className={`w-full h-[110px] p-3 border rounded-lg resize-none text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all font-normal ${notesErr
              ? "border-red-400 focus:ring-red-100 bg-red-50"
              : "border-slate-200 focus:ring-blue-100 focus:border-blue-400 bg-white"
              }`}
          />
          {notesErr && (
            <p className="text-red-500 text-xs mt-1">{notesErr}</p>
          )}
        </div>

        {/* ── Confirm ── */}
        <button
          type="button"
          disabled={loading}
          onClick={handleSubmit}
          className="w-full py-3.5 rounded-lg text-[14px] font-semibold text-white transition-colors disabled:opacity-60"
          style={{ backgroundColor: "#1e3a6e" }}
          onMouseOver={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
            "#2563eb")
          }
          onMouseOut={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
            "#1e3a6e")
          }
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingIcon icon="oval" className="w-4 h-4" color="white" />
              Confirming...
            </span>
          ) : (
            "Confirm"
          )}
        </button>

      </div>
    )}

      {/* ── Create Query Slideover ── */}
      <SlideoverModal
        visible={showCreateQuery}
        onClose={() => setShowCreateQuery(false)}
        headerTitle="Create New Query"
        size="xl"
        backdrop={true}
      >
        <CreateQuery
          onClose={() => setShowCreateQuery(false)}
          callerInfo={{
            caller: getCallerNum || "",
            sessionId: getSessionID || "",
            session_id: getSessionID || "",
          }}
        />
      </SlideoverModal>

      {/* ── View Query Modal ── */}
      <ViewQueryModal
        openViewModal={openViewModal}
        getqueryID={viewQueryID}
        handleClose={() => {
          setViewQueryID(null);
          setOpenViewModal(false);
        }}
      />
    </SlideoverModal>
  );
};

export default PostCallRemarks;